import React, { useState, useRef, useCallback } from "react";
import { Modal } from "@/components/ui/Modal";
import { Button } from "@/components/ui/Button";
import {
  useMediaUpload,
  FileUploadStatus,
  UploadResult,
} from "../hooks/useMediaUpload";
import { collectionItemCacheUtils } from "@/core/MediaLibrary/hooks/useCollectionItemsQuery";
import { UploadContentType } from "@/core/types/mediaContentType";

interface UploadMediaModalProps {
  isOpen: boolean;
  onClose: () => void;
  clientId: string;
  /**
   * Content type for uploads:
   * - "photos", "stories", "reels", "videos": Strict mode, validates file type matches
   * - "auto": Auto-detect from file extension (images→photos, videos→videos)
   */
  contentType: UploadContentType;
  /** When provided, uploaded items are auto-linked to this collection */
  collectionId?: string;
  onUploadComplete?: (results: UploadResult[]) => void;
}

const UploadIcon = () => (
  <svg
    className="w-12 h-12 text-gray-400"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={1.5}
      d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
    />
  </svg>
);

const FileIcon = ({ isVideo }: { isVideo: boolean }) => (
  <svg
    className="w-8 h-8 text-gray-400"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
  >
    {isVideo ? (
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.5}
        d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
      />
    ) : (
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.5}
        d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
      />
    )}
  </svg>
);

const StatusBadge = ({ status }: { status: FileUploadStatus["status"] }) => {
  const config = {
    pending: { bg: "bg-gray-100", text: "text-gray-600", label: "In attesa" },
    uploading: {
      bg: "bg-blue-100",
      text: "text-blue-600",
      label: "In caricamento",
    },
    complete: {
      bg: "bg-green-100",
      text: "text-green-600",
      label: "Completato",
    },
    failed: { bg: "bg-red-100", text: "text-red-600", label: "Fallito" },
  }[status];

  return (
    <span
      className={`px-2 py-0.5 text-xs font-medium rounded-full ${config.bg} ${config.text}`}
    >
      {config.label}
    </span>
  );
};

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function isVideoFile(filename: string): boolean {
  const ext = filename.toLowerCase().split(".").pop() || "";
  return ["mp4", "mov", "avi", "mkv", "webm"].includes(ext);
}

function isAudioFile(filename: string): boolean {
  const ext = filename.toLowerCase().split(".").pop() || "";
  return ["mp3", "wav", "aac", "flac", "ogg", "m4a", "wma"].includes(ext);
}

export const UploadMediaModal: React.FC<UploadMediaModalProps> = ({
  isOpen,
  onClose,
  clientId,
  contentType,
  collectionId,
  onUploadComplete,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [completedResults, setCompletedResults] = useState<UploadResult[]>([]);

  const handleUploadComplete = useCallback((result: UploadResult) => {
    if (collectionId) {
      collectionItemCacheUtils.invalidateCollectionItems(clientId, collectionId);
    }
    setCompletedResults((prev) => [...prev, result]);
  }, [clientId, collectionId]);

  const handleAllComplete = useCallback(() => {
    if (completedResults.length > 0) {
      onUploadComplete?.(completedResults);
    }
  }, [completedResults, onUploadComplete]);

  // Collection linking is handled server-side by the upload-finalizer job
  const {
    uploadFiles,
    fileStatuses,
    isUploading,
    cancelAll,
    cancelFile,
    retryFile,
    clearCompleted,
    clearAll,
  } = useMediaUpload({
    clientId,
    contentType,
    collectionId,
    onUploadComplete: handleUploadComplete,
    onAllComplete: handleAllComplete,
  });

  const acceptTypes =
    contentType === "photos"
      ? "image/jpeg,image/png,image/gif,image/webp,image/bmp,image/heic"
      : contentType === "audio"
        ? "audio/mpeg,audio/wav,audio/aac,audio/flac,audio/ogg,audio/mp4,audio/x-ms-wma"
        : contentType === "auto"
          ? "image/jpeg,image/png,image/gif,image/webp,image/bmp,image/heic,video/mp4,video/quicktime,video/x-msvideo,video/x-matroska,video/webm,audio/mpeg,audio/wav,audio/aac,audio/flac,audio/ogg,audio/mp4"
          : contentType === "stories"
            ? "image/jpeg,image/png,image/gif,image/webp,image/bmp,image/heic,video/mp4,video/quicktime,video/x-msvideo,video/x-matroska,video/webm"
            : "video/mp4,video/quicktime,video/x-msvideo,video/x-matroska,video/webm";

  const contentLabel =
    contentType === "photos"
      ? "images"
      : contentType === "videos"
        ? "videos"
        : contentType === "audio"
          ? "audio files"
          : contentType === "stories"
            ? "story media"
            : contentType === "auto"
              ? "media"
              : contentType;

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);

      const files = Array.from(e.dataTransfer.files);
      if (files.length > 0) {
        uploadFiles(files);
      }
    },
    [uploadFiles]
  );

  const handleFileSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files ? Array.from(e.target.files) : [];
      if (files.length > 0) {
        uploadFiles(files);
      }
      // Reset input so the same file can be selected again
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    },
    [uploadFiles]
  );

  const handleClose = useCallback(() => {
    if (isUploading) {
      cancelAll();
    }
    clearAll();
    setCompletedResults([]);
    onClose();
  }, [isUploading, cancelAll, clearAll, onClose]);

  const hasFiles = fileStatuses.length > 0;
  const hasCompleted = fileStatuses.some((fs) => fs.status === "complete");
  const hasFailed = fileStatuses.some((fs) => fs.status === "failed");
  const allComplete =
    hasFiles && fileStatuses.every((fs) => fs.status === "complete");

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title={`Upload ${contentLabel}`}>
      <div className="p-6 space-y-4">
        {/* Drop zone */}
        <div
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
          className={`
            relative border-2 border-dashed rounded-lg p-8 text-center cursor-pointer
            transition-colors duration-200
            ${
              isDragging
                ? "border-blue-500 bg-blue-50"
                : "border-gray-300 hover:border-gray-400 hover:bg-gray-50"
            }
            ${isUploading ? "pointer-events-none opacity-50" : ""}
          `}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept={acceptTypes}
            multiple
            onChange={handleFileSelect}
            className="hidden"
            disabled={isUploading}
          />

          <div className="flex flex-col items-center gap-3">
            <UploadIcon />
            <div>
              <p className="text-sm font-medium text-gray-700">
                {isDragging
                  ? `Drop ${contentLabel} here`
                  : `Drag and drop ${contentLabel} here`}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                or click to browse files
              </p>
            </div>
            <p className="text-xs text-gray-400">
              {contentType === "photos"
                ? "JPG, PNG, GIF, WebP, BMP, HEIC up to 100MB"
                : contentType === "audio"
                  ? "MP3, WAV, AAC, FLAC, OGG, M4A up to 100MB"
                  : contentType === "auto"
                    ? "Images (up to 100MB), Videos (up to 50GB), or Audio (up to 100MB)"
                    : "MP4, MOV, AVI, MKV, WebM up to 50GB"}
            </p>
          </div>
        </div>

        {/* File list */}
        {hasFiles && (
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {fileStatuses.map((fs, index) => (
              <div
                key={`${fs.file.name}-${index}`}
                className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg"
              >
                <FileIcon isVideo={isVideoFile(fs.file.name)} />

                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {fs.file.name}
                  </p>
                  <p className="text-xs text-gray-500">
                    {formatFileSize(fs.file.size)}
                    {fs.error && (
                      <span className="text-red-500 ml-2">{fs.error}</span>
                    )}
                  </p>

                  {/* Progress bar */}
                  {fs.status === "uploading" && (
                    <div className="mt-1 w-full bg-gray-200 rounded-full h-1">
                      <div
                        className="bg-blue-600 h-1 rounded-full transition-all duration-300"
                        style={{ width: `${fs.progress}%` }}
                      />
                    </div>
                  )}
                </div>

                <StatusBadge status={fs.status} />

                {/* Actions */}
                <div className="flex items-center gap-1">
                  {fs.status === "uploading" && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        cancelFile(fs.file);
                      }}
                      className="p-1 text-gray-400 hover:text-gray-600"
                      title="Cancel"
                    >
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </button>
                  )}

                  {fs.status === "failed" && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        retryFile(fs.file);
                      }}
                      className="p-1 text-gray-400 hover:text-blue-600"
                      title="Retry"
                    >
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                        />
                      </svg>
                    </button>
                  )}

                  {fs.status === "complete" && (
                    <svg
                      className="w-5 h-5 text-green-500"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Actions */}
        <div className="flex justify-between items-center pt-2">
          <div className="flex gap-2">
            {hasCompleted && (
              <Button variant="ghost" size="sm" onClick={clearCompleted}>
                Clear completed
              </Button>
            )}
            {hasFailed && !isUploading && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  fileStatuses
                    .filter((fs) => fs.status === "failed")
                    .forEach((fs) => retryFile(fs.file));
                }}
              >
                Retry failed
              </Button>
            )}
          </div>

          <div className="flex gap-2">
            {isUploading && (
              <Button variant="secondary" size="sm" onClick={cancelAll}>
                Cancel all
              </Button>
            )}
            <Button
              variant={allComplete ? "primary" : "secondary"}
              size="sm"
              onClick={handleClose}
            >
              {allComplete ? "Done" : "Close"}
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};
